import { Chip, type ChipProps } from '@mui/material';
import { styled } from '@mui/material/styles';
import type { Theme } from '@mui/material/styles';
import { MatterStatus } from '~/gql/graphql';
import { getMatterStatusDisplayLabel } from '~/shared/utils/matterDisplay';

export function getMatterStatusColor(
  theme: Theme,
  status: MatterStatus,
): { bgcolor: string; color: string } {
  return getMatterStatusColorsFromTheme(theme, status);
}

const FALLBACK_STATUS_COLORS = { bgcolor: '#eeeeee', color: '#616161' };

function getMatterStatusColorsFromTheme(
  theme: Theme,
  status: MatterStatus,
): { bgcolor: string; color: string } {
  const statusPalette = theme.palette.matterStatus;
  if (!statusPalette) {
    return FALLBACK_STATUS_COLORS;
  }
  switch (status) {
    case MatterStatus.Active: {
      return {
        bgcolor: statusPalette.active.bg,
        color: statusPalette.active.text,
      };
    }
    case MatterStatus.Inactive: {
      return {
        bgcolor: statusPalette.inactive.bg,
        color: statusPalette.inactive.text,
      };
    }
    case MatterStatus.Closed: {
      return {
        bgcolor: statusPalette.closed.bg,
        color: statusPalette.closed.text,
      };
    }
    default: {
      return {
        bgcolor: statusPalette.closed.bg,
        color: statusPalette.closed.text,
      };
    }
  }
}

interface Props extends Omit<ChipProps, 'label'> {
  status: MatterStatus;
}

export const MatterStatusChip = ({
  status,
  size = 'small',
  ...rest
}: Props) => {
  return (
    <StyledChip
      label={getMatterStatusDisplayLabel(status)}
      size={size}
      status={status}
      {...rest}
    />
  );
};

const StyledChip = styled(Chip, {
  shouldForwardProp: (prop) => prop !== 'status',
})<{ status: MatterStatus }>(({ theme, status }) => {
  const { bgcolor, color } = getMatterStatusColorsFromTheme(theme, status);
  return {
    bgcolor,
    color,
    fontWeight: 600,
    height: 22,
    '& .MuiChip-label': {
      padding: theme.spacing(0, 1),
      fontSize: theme.typography.caption.fontSize,
    },
  };
});
